import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Renders a list of items, showing only the first `initial` (default 5).
 * A "Show all N" toggle expands to show the rest; "Collapse" hides them again.
 *
 * Usage:
 *   <CollapsibleList items={tickets} keyFn={t => t.id} initial={5} testId="lottery">
 *     {(t, i) => <TicketRow key={t.id} ticket={t} idx={i} />}
 *   </CollapsibleList>
 *
 * Props:
 *   items     Array — the data
 *   initial   Number — how many to show collapsed (default 5)
 *   children  (item, i) => ReactNode — renderer per item
 *   testId    string — suffix for data-testid on the toggle button
 *   emptyText string — shown when items.length === 0 (optional)
 */
export const CollapsibleList = ({
    items = [],
    initial = 5,
    children,
    testId = 'list',
    emptyText,
    className = '',
}) => {
    const [expanded, setExpanded] = useState(false);
    if (!Array.isArray(items) || items.length === 0) {
        if (emptyText) {
            return <p className="text-xs text-[#A0AAB2] italic py-2">{emptyText}</p>;
        }
        return null;
    }
    const overflow = items.length > initial;
    const shown = expanded || !overflow ? items : items.slice(0, initial);
    return (
        <div className={className}>
            {shown.map((item, i) => children(item, i))}
            {overflow && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="w-full mt-2 py-2 px-3 rounded-md border border-dashed border-white/10 hover:border-white/30 text-[11px] text-[#A0AAB2] hover:text-white transition-colors flex items-center justify-center gap-1"
                    data-testid={`collapsible-${testId}-toggle`}
                >
                    {expanded ? (
                        <>Collapse <ChevronUp className="w-3 h-3" /></>
                    ) : (
                        <>Show all {items.length} <ChevronDown className="w-3 h-3" /></>
                    )}
                </button>
            )}
        </div>
    );
};

export default CollapsibleList;
